import { FormApi } from 'final-form'
import { DropdownInput, NativeMountingInputs, TextFieldInput } from 'types/mounting'
import { StudioSystemType } from 'types/studio/items'
import { createDlubalSnowLoadButtonConfig, createDlubalWindSpeedButtonConfig } from '../../../dlubal'
import { YesNo } from './PZSESchema'
import { getSnapnrackMountingAttachmentFieldSpecs } from './SnapnrackPZSEInputForm'
import {
  ULTRA_RAIL_COMPONENT_TITLES,
} from './UltraRailAttachments'
import { getFastenersForAttachment } from './UltraRailFasteners'
import {
  getEndClampOptions,
  getMidClampOptions,
  getProfileFromRailTypeCode,
  getRailTypeOptionsForForm,
  getSpliceFinishOptions,
} from './UltraRailInputOptions'
import type { LabelValueOption, UltraRailMountingType, UltraRailRailType } from './UltraRailTypes'
import type { SnapnrackPZSESelectField } from './SnapnrackPZSEInputForm'

function snapnrackSelectFieldToDropdown(field: SnapnrackPZSESelectField): DropdownInput {
  return {
    label: field.label,
    variableName: field.variableName,
    default: field.default,
    options: field.options,
  }
}

function buildSnapnrackDropdownInputs(params: {
  railTypeOptions: LabelValueOption[]
  defaultRailTypeValue: string
  spliceFinishOptions: LabelValueOption[]
  mountingTypeField: SnapnrackPZSESelectField
  attachmentField: SnapnrackPZSESelectField
  fastenerOptions: LabelValueOption[]
  endClampOptions: LabelValueOption[]
  midClampOptions: LabelValueOption[]
  prioritiseDomesticContent: YesNo
  universalSkirtRailOptions: { label: string; value: string }[]
  skirtRailSpacerOptions: { label: string; value: string }[]
  showSkirtRailSpacer: boolean
}): DropdownInput[] {
  const {
    railTypeOptions,
    defaultRailTypeValue,
    spliceFinishOptions,
    mountingTypeField,
    attachmentField,
    fastenerOptions,
    endClampOptions,
    midClampOptions,
    prioritiseDomesticContent,
    universalSkirtRailOptions,
    skirtRailSpacerOptions,
    showSkirtRailSpacer,
  } = params
  return [
    {
      label: 'Rail run',
      variableName: 'railRun',
      default: '2 Rail',
      options: [
        { label: '2 Rail', value: '2 Rail' },
        { label: '3 Rail', value: '3 Rail' },
      ],
    },
    {
      label: 'Prioritise domestic content?',
      variableName: 'prioritiseDomesticContent',
      default: 'No',
      options: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
      ],
    },
    {
      label: 'Rail type',
      variableName: 'railType',
      default: defaultRailTypeValue,
      options: railTypeOptions.length > 0 ? railTypeOptions : [{ label: '—', value: '' }],
    },
    {
      label: 'End clamp',
      variableName: 'endClamp',
      default: prioritiseDomesticContent ? endClampOptions[0].value : '242-02215',
      options: endClampOptions,
    },
    {
      label: 'Mid clamp',
      variableName: 'midClamp',
      default: prioritiseDomesticContent ? midClampOptions[0].value : '242-02071',
      options: midClampOptions,
    },
    ...(spliceFinishOptions.length > 0
      ? [
          {
            label: 'Splice connector finish',
            variableName: 'spliceFinish',
            default: spliceFinishOptions[0]?.value ?? '',
            options: spliceFinishOptions.map((o) => ({ label: o.label, value: o.value })),
          },
        ]
      : []),
    snapnrackSelectFieldToDropdown(mountingTypeField),
    snapnrackSelectFieldToDropdown(attachmentField),
    {
      label: 'Fasteners',
      variableName: 'fastener',
      default: fastenerOptions[0]?.value ?? '',
      options: fastenerOptions.length > 0 ? fastenerOptions : [{ label: '—', value: '' }],
    },
    {
      label: 'Skirt Rail',
      variableName: 'universalSkirtRail',
      default: 'None',
      options: universalSkirtRailOptions,
    },
    ...(showSkirtRailSpacer
      ? [
          {
            label: 'Skirt Rail Spacer',
            variableName: 'skirtRailSpacer',
            default: skirtRailSpacerOptions[0].value,
            options: skirtRailSpacerOptions,
          },
        ]
      : []),
    {
      label: 'Cable managers',
      variableName: 'cableManagers',
      default: 'No',
      options: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
      ],
    },
  ]
}

export function getSnapnrackDlubalTextFieldInputs(): TextFieldInput[] {
  return [
    {
      label: 'Wind Speed (mph)',
      variableName: 'windSpeed',
      type: 'number',
      default: 0,
      button: createDlubalWindSpeedButtonConfig(['snapnrackUltraRail']),
    },
    {
      label: 'Design Snow Load (psf)',
      variableName: 'designSnowLoad',
      type: 'number',
      default: 0,
      button: createDlubalSnowLoadButtonConfig(['snapnrackUltraRail']),
    },
  ]
}

export function createSnapnrackInputs(): (projectForm: FormApi, system: StudioSystemType) => NativeMountingInputs {
  return (_projectForm, system) => {
    const integrationData = (system?.integration_json?.snapnrackUltraRail || {}) as Record<string, unknown>

    const prioritiseDomesticContent = (integrationData.prioritiseDomesticContent ?? 'No') as YesNo
    const currentRailTypeValue = integrationData.railType as string | undefined
    const railTypeOptions = getRailTypeOptionsForForm(prioritiseDomesticContent, currentRailTypeValue)
    const defaultRailTypeValue = prioritiseDomesticContent === 'Yes' ? '232-10096-USA' : '232-10096' // UR-45 Black 172"

    const mountingType = (integrationData.mountingType ?? 'Rafter') as UltraRailMountingType
    const { mountingType: mountingTypeField, attachment: attachmentField } =
      getSnapnrackMountingAttachmentFieldSpecs(system)
    const profileForSplice = getProfileFromRailTypeCode(currentRailTypeValue) ?? ('UR-45' as UltraRailRailType)

    const selectedAttachmentCode = (integrationData.attachment ?? attachmentField.default) as string

    const fastenerSpecs = getFastenersForAttachment(selectedAttachmentCode, mountingType)
    const fastenerOptions = fastenerSpecs.map((spec) => ({
      label: ULTRA_RAIL_COMPONENT_TITLES[spec.code] || spec.code,
      value: spec.code,
    }))

    const spliceFinishOptions = getSpliceFinishOptions(profileForSplice, prioritiseDomesticContent)
    const endClampOptions = getEndClampOptions(prioritiseDomesticContent)
    const midClampOptions = getMidClampOptions(prioritiseDomesticContent)

    const universalSkirtRailOptions = [
      { label: 'None', value: 'None' },
      { label: 'Universal Skirt Rail 83"', value: '232-02493-USA' },
    ]
    const skirtRailSpacerOptions: { label: string; value: string }[] = [
      { label: 'Universal SkirtRail Spacer, 30MM (USA)', value: '232-02532-USA' },
      { label: 'Universal SkirtRail Spacer, 32MM (USA)', value: '232-02494-USA' },
      { label: 'Universal SkirtRail Spacer, 35MM (USA)', value: '232-02495-USA' },
      { label: 'Universal SkirtRail Spacer, 38MM (USA)', value: '232-02496-USA' },
      { label: 'Universal SkirtRail Spacer, 40MM (USA)', value: '232-02497-USA' },
    ]
    const showSkirtRailSpacer =
      integrationData.universalSkirtRail !== 'None' && integrationData.universalSkirtRail != null

    return {
      dropdownInputs: buildSnapnrackDropdownInputs({
        railTypeOptions,
        defaultRailTypeValue,
        spliceFinishOptions,
        mountingTypeField,
        attachmentField,
        fastenerOptions,
        endClampOptions,
        midClampOptions,
        prioritiseDomesticContent,
        universalSkirtRailOptions,
        skirtRailSpacerOptions,
        showSkirtRailSpacer,
      }),
    }
  }
}
